import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import styles from './calculadora.module.css';
import { FiPercent, FiDivide, FiX, FiMinus, FiPlus } from 'react-icons/fi';
import ui from "../../styles/ui.module.css";


const Calculadora = ({ onClose }) => {
  const [calculo, setCalculo] = useState('');
  const [resultado, setResultado] = useState(null); // número
  const contentRef = useRef(null);

  // Fecha ao clicar fora do conteúdo
  const handleOverlayClick = (e) => {
    if (e.currentTarget === e.target) {
      onClose(null);
    }
  };

  // Avaliador seguro de expressões (+,-,*,/, parênteses) com suporte a unário
  const evaluateExpression = (input) => {
    let i = 0;
    const s = input.replace(/\s+/g, '');

    const peek = () => s[i];
    const next = () => s[i++];

    const parseNumber = () => {
      let start = i;
      // número: [digits][.digits]
      while (/[0-9]/.test(peek())) next();
      if (peek() === '.') {
        next();
        while (/[0-9]/.test(peek())) next();
      }
      if (i === start) throw new Error('Número esperado');
      return parseFloat(s.slice(start, i));
    };

    const parseFactor = () => {
      const ch = peek();
      if (ch === '+' || ch === '-') {
        const sign = next();
        const val = parseFactor();
        return sign === '-' ? -val : val;
      }
      if (ch === '(') {
        next();
        const val = parseExpr();
        if (peek() !== ')') throw new Error('Parêntese não fechado');
        next();
        return val;
      }
      return parseNumber();
    };

    const parseTerm = () => {
      let val = parseFactor();
      while (peek() === '*' || peek() === '/') {
        const op = next();
        const rhs = parseFactor();
        if (op === '*') val = val * rhs;
        else val = val / rhs;
      }
      return val;
    };

    const parseExpr = () => {
      let val = parseTerm();
      while (peek() === '+' || peek() === '-') {
        const op = next();
        const rhs = parseTerm();
        if (op === '+') val = val + rhs;
        else val = val - rhs;
      }
      return val;
    };

    const result = parseExpr();
    if (i < s.length) throw new Error('Expressão inválida');
    return result;
  };

  // Sanitiza expressão permitindo apenas números, operadores e %
  const sanitizeExpression = (expr) => {
    const normalized = expr.replace(/,/g, '.');
    const allowed = /^[0-9.+\-*/()%\s]*$/;
    if (!allowed.test(normalized)) throw new Error('Caracter inválido');
    // Remove operadores finais que quebrariam o eval
    let cleaned = normalized.trim();
    while (/[+\-*/.]$/.test(cleaned)) cleaned = cleaned.slice(0, -1);
    return cleaned;
  };

  // Remove operadores finais repetidos
  const stripTrailingOperators = (input) => {
    let value = input;
    while (/[+\-*/.]$/.test(value)) value = value.slice(0, -1);
    return value;
  };

  // Aplica regra de porcentagem contextual:
  // A + B% => A + (A * (B/100)), A - B% => A - (A * (B/100))
  // A * B% => A * (B/100), A / B% => A / (B/100)
  // X% avulso => (X/100)
  const applyPercentContext = (expr) => {
    let out = expr.replace(/,/g, '.');
    const re = /(\d+(?:\.\d+)?)\s*([+\-*/])\s*(\d+(?:\.\d+)?)%/;
    // Processa iterativamente da esquerda para a direita
    while (re.test(out)) {
      out = out.replace(re, (_, a, op, b) => {
        switch (op) {
          case '+':
            return `(${a} + (${a} * (${b}/100)))`;
          case '-':
            return `(${a} - (${a} * (${b}/100)))`;
          case '*':
            return `(${a} * (${b}/100))`;
          case '/':
            return `(${a} / (${b}/100))`;
          default:
            return _;
        }
      });
    }
    // Demais ocorrências tipo X%
    out = out.replace(/(\d+(?:\.\d+)?)%/g, '($1/100)');
    return out;
  };

  // Formata com 2 casas decimais
  const toFixed2 = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return '0.00';
    const rounded = Math.round(num * 100) / 100;
    return rounded.toFixed(2);
  };

  const displayValue = resultado !== null ? toFixed2(resultado) : (calculo || '0');

  // Adiciona caracteres ao cálculo
  const adicionarAoCalculo = (valor) => {
    setCalculo((prev) => {
      const isDigitOrComma = /^[0-9.,]$/.test(valor);
      if (resultado !== null && isDigitOrComma) {
        return valor;
      }
      return `${prev}${valor}`;
    });
    setResultado(null);
  };

  // Limpa todo o campo
  const apagarTudo = () => {
    setCalculo('');
    setResultado(null);
  };

  // Apaga apenas o último caractere
  const apagarUltimo = () => {
    setCalculo(prev => prev.slice(0, -1));
    setResultado(null);
  };

  // Faz o parse do cálculo, substitui X% por (X/100) e avalia a expressão
  const calcularResultado = () => {
    try {
      const raw = calculo.trim();
      if (!raw) {
        toast.warn('Digite uma expressão válida antes de calcular');
        return;
      }
      const percentParsed = applyPercentContext(raw);
      const expr = sanitizeExpression(percentParsed);
      const safeExpr = expr || stripTrailingOperators(percentParsed);
      if (!safeExpr) {
        toast.warn('Digite uma expressão válida antes de calcular');
        return;
      }
      const r = evaluateExpression(safeExpr);
      setResultado(parseFloat(toFixed2(r)));
      setCalculo(toFixed2(r));
    } catch (err) {
      toast.error('Erro no cálculo: ' + err.message);
    }
  };

  // Ao clicar em “OK”:
  // - se já houver um resultado calculado, envia-o
  // - senão, tenta avaliar o current “calculo” uma última vez
  const handleOk = () => {
    if (resultado !== null) {
      onClose(parseFloat(toFixed2(resultado)));
      return;
    }
    try {
      const raw = calculo.trim();
      if (!raw) {
        toast.warn('Digite uma expressão válida antes de calcular');
        return;
      }
      const percentParsed = applyPercentContext(raw);
      const expr = sanitizeExpression(percentParsed);
      const safeExpr = expr || stripTrailingOperators(percentParsed);
      if (!safeExpr) {
        toast.warn('Digite uma expressão válida antes de calcular');
        return;
      }
      const r = evaluateExpression(safeExpr);
      onClose(parseFloat(toFixed2(r)));
    } catch {
      toast.error('Expressão inválida');
    }
  };

  // Quando o usuário clicar no “X” do modal, simplesmente fecha sem resultado
  const handleFechar = () => {
    onClose(null);
  };

  // Suporte ao teclado: números, operadores, Enter (=), Backspace (C), Delete (AC), Esc (fechar)
  useEffect(() => {
    const onKeyDown = (e) => {
      const { key, code, keyCode, which } = e;
      if (/^[0-9]$/.test(key)) {
        e.preventDefault();
        adicionarAoCalculo(key);
        return;
      }
      if (["+", "-", "*", "/", ".", ","].includes(key)) {
        e.preventDefault();
        adicionarAoCalculo(key);
        return;
      }
      if (key === "%") {
        e.preventDefault();
        adicionarAoCalculo('%');
        return;
      }
      const isEnterKey = key === "Enter" || code === "Enter" || code === "NumpadEnter" || key === "NumpadEnter";
      const isEqualKey = key === "=" || key === "Equal";
      if (isEnterKey || isEqualKey || keyCode === 13 || which === 13 || keyCode === 187 || which === 187) {
        e.preventDefault();
        calcularResultado();
        return;
      }
      if (key === "Backspace") {
        e.preventDefault();
        apagarUltimo();
        return;
      }
      if (key === "Delete") {
        e.preventDefault();
        apagarTudo();
        return;
      }
      if (key === "Escape") {
        e.preventDefault();
        handleFechar();
        return;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Focus-trap básico no modal
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const getFocusable = () => Array.from(
      container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => !el.hasAttribute('disabled'));

    const focusables = getFocusable();
    if (focusables.length) {
      focusables[0].focus();
    }

    const handleTrap = (e) => {
      if (e.key !== 'Tab') return;
      const items = getFocusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    container.addEventListener('keydown', handleTrap);
    return () => container.removeEventListener('keydown', handleTrap);
  }, []);

  return (
    <div className={styles["modal"]} role="dialog" aria-modal="true" aria-labelledby="calc-title" onClick={handleOverlayClick}>
      <div className={styles["modal-content"]} ref={contentRef}>
        <button onClick={handleFechar} className={`${ui.butfechar}`} aria-label="Fechar calculadora">
          <FiX size={24} />
        </button>
        <h3 className={`${styles['calc-title']}`} id="calc-title">Calculadora</h3>

        <div className={styles["calc-display"]} aria-live="polite">
          {displayValue}
        </div>

        {/* Exibe o resultado abaixo, se já foi calculado */}
        {/*{resultado !== null && (
          <p className={styles["calc-resultado"]}>
            = {toFixed2(resultado)}
          </p>
        )}*/}

        <div className={styles["calc-grid"]}>
          <button className={styles['btn-top']} onClick={apagarTudo}>AC</button>
          <button className={styles['btn-top']} onClick={apagarUltimo}>C</button>
          <button className={styles['btn-top']} onClick={() => adicionarAoCalculo('%')}><FiPercent /></button>
          <button className={styles['btn-operator']} onClick={() => adicionarAoCalculo('/')}><FiDivide /></button>

          <button className={styles['btn-digit']} onClick={() => adicionarAoCalculo('7')}>7</button>
          <button className={styles['btn-digit']} onClick={() => adicionarAoCalculo('8')}>8</button>
          <button className={styles['btn-digit']} onClick={() => adicionarAoCalculo('9')}>9</button>
          <button className={styles['btn-operator']} onClick={() => adicionarAoCalculo('*')}><FiX /></button>

          <button className={styles['btn-digit']} onClick={() => adicionarAoCalculo('4')}>4</button>
          <button className={styles['btn-digit']} onClick={() => adicionarAoCalculo('5')}>5</button>
          <button className={styles['btn-digit']} onClick={() => adicionarAoCalculo('6')}>6</button>
          <button className={styles['btn-operator']} onClick={() => adicionarAoCalculo('-')}><FiMinus /></button>

          <button className={styles['btn-digit']} onClick={() => adicionarAoCalculo('1')}>1</button>
          <button className={styles['btn-digit']} onClick={() => adicionarAoCalculo('2')}>2</button>
          <button className={styles['btn-digit']} onClick={() => adicionarAoCalculo('3')}>3</button>
          <button className={styles['btn-operator']} onClick={() => adicionarAoCalculo('+')}><FiPlus /></button>

          <button className={`${styles['btn-wide']} ${styles['btn-digit']}`} onClick={() => adicionarAoCalculo('0')}>0</button>
          <button className={styles['btn-digit']} onClick={() => adicionarAoCalculo('.')}>.</button>
          <button className={styles['btn-operator']} onClick={calcularResultado}>=</button>
          <button className={styles['btn-action']} onClick={handleOk}>OK</button>
        </div>

        {/*<div className={styles["calc-resulteok"]}>
          <button onClick={handleOk}>OK</button>
        </div>*/}
      </div>
    </div>
  );
};

export default Calculadora;
